import { Zap, Sunrise, Sun, Moon, CalendarDays, Clock } from "lucide-react";

export const DUE_OPTIONS = [
  { value: "nu", label: "Nu", icon: Zap, color: "bg-brand-pink", text: "text-brand-ink" },
  { value: "ochtend", label: "Ochtend", icon: Sunrise, color: "bg-brand-yellow", text: "text-brand-ink" },
  { value: "middag", label: "Middag", icon: Sun, color: "bg-brand-orange", text: "text-white" },
  { value: "avond", label: "Avond", icon: Moon, color: "bg-brand-blue", text: "text-white" },
  { value: "vandaag", label: "Vandaag", icon: CalendarDays, color: "bg-brand-green", text: "text-brand-ink" },
  { value: "specifiek", label: "Tijdstip", icon: Clock, color: "bg-white", text: "text-brand-ink" },
];

export const dueMeta = (v) => DUE_OPTIONS.find((d) => d.value === v) || DUE_OPTIONS[4];

// Overdue-info voor een taak: verlopen dag-slot dat nog niet is afgerond.
export const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const taskEffDate = (task) => {
  if (task.next_date) return task.next_date;
  if (!task.created_at) return null;
  const d = new Date(task.created_at);
  return isNaN(d) ? String(task.created_at).slice(0, 10) : localISO(d);
};
export const weekdayDayLabel = (isoStr, loc = "nl-NL") => {
  if (!isoStr) return "";
  const d = new Date(isoStr + "T00:00:00");
  if (isNaN(d)) return "";
  const wd = d.toLocaleDateString(loc, { weekday: "long" });
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const far = Math.abs(Math.round((d - now) / 864e5)) > 6;
  const mon = far ? ` ${d.toLocaleDateString(loc, { month: "short" }).replace(".", "")}` : "";
  return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.getDate()}${mon}`;
};

export const taskOverdueInfo = (task) => {
  const settled = task.status === "klaar" || task.status === "wacht_op_bevestiging";
  const daySlots = ["vandaag", "nu", "ochtend", "middag", "avond", "specifiek"];
  const effDate = taskEffDate(task);
  let daysOver = 0;
  if (effDate) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const eff = new Date(effDate + "T00:00:00");
    daysOver = Math.round((today - eff) / 864e5);
  }
  const overdue = !settled && daysOver >= 1 && daySlots.includes(task.due_when);
  return { overdue, daysOver, effDate };
};

// Map a manual "HH:MM" time to its part of the day (dagdeel).
export const dagdeelFromTime = (time) => {
  if (!time) return null;
  const h = parseInt(String(time).split(":")[0], 10);
  if (Number.isNaN(h)) return null;
  if (h >= 5 && h < 12) return "ochtend";
  if (h >= 12 && h < 18) return "middag";
  return "avond";
};

// Effective dagdeel for grouping/filtering: a "specifiek" task falls into its dagdeel.
export const effectiveDagdeel = (task) =>
  task.due_when === "specifiek" ? (dagdeelFromTime(task.due_time) || "specifiek") : task.due_when;

// Kleuren per dagdeel voor pindates in de agenda (consistent met DUE_OPTIONS).
export const PINDATE_DAYPARTS = [
  { value: "ochtend", label: "Ochtend", cls: "bg-brand-yellow", text: "text-brand-ink", hex: "#FBBF24", ink: "#1a1a1a" },
  { value: "middag", label: "Middag", cls: "bg-brand-orange", text: "text-white", hex: "#371F75", ink: "#FFFFFF" },
  { value: "avond", label: "Avond", cls: "bg-brand-blue", text: "text-white", hex: "#3B82F6", ink: "#FFFFFF" },
];
export const PINDATE_NEUTRAL = { value: "hele_dag", label: "Hele dag", cls: "bg-brand-teal", text: "text-brand-ink", hex: "#2DD4BF", ink: "#1a1a1a" };

// Dagdeel van een pindate: exacte tijd -> afgeleid dagdeel, anders het gekozen dagdeel.
export const pindateDagdeel = (p) => {
  if (!p) return null;
  if (p.event_time) return dagdeelFromTime(p.event_time);
  if (p.event_daypart) return p.event_daypart;
  return null;
};

// Kleurmeta voor een pindate op basis van dagdeel (neutraal bij geen tijd/dagdeel).
export const pindateColor = (p) => PINDATE_DAYPARTS.find((x) => x.value === pindateDagdeel(p)) || PINDATE_NEUTRAL;


export const RECURRENCE = [
  { value: "dagelijks", label: "Dagelijks" },
  { value: "om_de_dag", label: "Om de dag" },
  { value: "wekelijks", label: "Wekelijks" },
  { value: "twee_wekelijks", label: "Twee-wekelijks" },
  { value: "maandelijks", label: "Maandelijks" },
];

export const WEEKDAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];

export const ROLE_LABELS = { super_admin: "Super Admin", beheerder: "Beheerder", gebruiker: "Gebruiker" };

export const MODULE_TOGGLES = [
  { key: "nieuws", label: "Nieuws (Story)" },
  { key: "mededelingen", label: "Mededelingen" },
  { key: "vieringen", label: "Vieringen" },
  { key: "taakruilingen", label: "Taakruilingen" },
  { key: "pindates", label: "Pin Dates" },
  { key: "activiteiten", label: "Activiteiten" },
  { key: "onderhoud", label: "Onderhoud" },
  { key: "kennisbank", label: "Hoedan, Wattan" },
  { key: "klusjes", label: "Klusjes" },
  { key: "ruilmarkt", label: "Ruilmarkt" },
  { key: "rooster", label: "Rooster" },
  { key: "eten", label: "Eten (recepten & boodschappen)" },
  { key: "polls", label: "Stemmingen / Polls" },
  { key: "hoppa", label: "Hoppa (marktplaats)" },
];


export const CONTENT_MODULES = {
  mededelingen: { title: "Mededelingen", sub: "Deel nieuws met de groep", icon: "Megaphone", color: "yellow", hasDate: false },
  vieringen: { title: "Vieringen", sub: "Verjaardagen & feestjes", icon: "PartyPopper", color: "pink", hasDate: true },
  taakruilingen: { title: "Taakruilingen", sub: "Ruil taken met elkaar", icon: "Repeat", color: "green", hasDate: false },
  pindates: { title: "Pin Dates", sub: "Belangrijke gebeurtenissen", icon: "CalendarClock", color: "blue", hasDate: true, hasLog: true },
  activiteiten: { title: "Activiteiten", sub: "Registreer deelname", icon: "Activity", color: "orange", hasDate: true },
  onderhoud: { title: "Onderhoud", sub: "Wat, wanneer & logboek", icon: "Wrench", color: "white", hasDate: true, hasLog: true },
  kennisbank: { title: "Hoedan, Wattan", sub: "Hoe werkt het hier?", icon: "BookOpen", color: "green", hasLog: false },
};

export const PERSON_COLORS = [
  { bg: "#7C3AED", text: "#ffffff" },
  { bg: "#EC4899", text: "#ffffff" },
  { bg: "#0EA5E9", text: "#ffffff" },
  { bg: "#14B8A6", text: "#042f2e" },
  { bg: "#F59E0B", text: "#3d2c00" },
  { bg: "#EF4444", text: "#ffffff" },
  { bg: "#10B981", text: "#052e1b" },
  { bg: "#F97316", text: "#3d1c00" },
  { bg: "#3B82F6", text: "#ffffff" },
  { bg: "#D946EF", text: "#ffffff" },
  { bg: "#84CC16", text: "#1a2e05" },
  { bg: "#6366F1", text: "#ffffff" },
];

export function userColor(id = "") {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[h % PERSON_COLORS.length];
}

export const DAYPART_LABELS = { ochtend: "Ochtend", middag: "Middag", avond: "Avond" };

export const SHOP_CATEGORIES = ["Groente & Fruit", "Zuivel", "Vlees & Vis", "Brood & Bakkerij", "Dranken", "Diepvries", "Kruidenier", "Overig"];

// Verhoog/verlaag het getal vooraan een vrije-tekst hoeveelheid ("2 pakken" -> "3 pakken"), eenheid blijft behouden.
export const stepQuantity = (q, delta) => {
  const s = String(q || "").trim();
  const m = s.match(/^(\d+(?:[.,]\d+)?)(.*)$/);
  if (!m) {
    const n = Math.max(0, 1 + delta);
    return n <= 0 ? "" : (s ? `${n} ${s}` : String(n));
  }
  let num = parseFloat(m[1].replace(",", ".")) + delta;
  if (num <= 0) return "";
  const numStr = Number.isInteger(num) ? String(num) : String(num).replace(".", ",");
  return `${numStr}${m[2]}`;
};

// Weer te geven tijd-label voor een pin date: exacte tijd > dagdeel-label > niets.
export const pinTimeLabel = (p) => {
  if (!p) return "";
  if (p.event_time) return `${p.event_time}${p.event_end_time ? `–${p.event_end_time}` : ""}`;
  return DAYPART_LABELS[p.event_daypart] || "";
};

